import { getLocalApi } from "./localApi";
import apiConfig from "app/api/apiConfig.json";
import {
  mapDateStrategyAssignments,
  mapPackageOfferingBreakdowns,
  resolveFeeUnitTypeId,
} from "app/pages/PricelistNew/domain/draft/normalizeDraftRow";
import { normalizeRecurranceStartDays } from "app/pages/PricelistNew/domain/dateStrategy/recurranceDays";

function hasDecommissionDateSet(row: any): boolean {
  const d = row?.decommissionDate;
  if (d == null) return false;
  if (typeof d === "string" && d.trim() === "") return false;
  return true;
}

export interface PricingYearDateRange {
  dateFrom: string;
  dateTo: string;
}

export interface PricingYearHighSeasonOverride {
  centreId: number;
  dateFrom: string;
  dateTo: string;
}

export interface PricingYearHighSeasonEntry {
  CountryIsoCode: string;
  dateFrom: string;
  dateTo: string;
  centreOverrides?: PricingYearHighSeasonOverride[];
}

export interface DraftPricingYearDetails {
  divisionId: number;
  year: number;
  academicYearDates: {
    courses: PricingYearDateRange | null;
    accommodation: PricingYearDateRange | null;
    addOns: PricingYearDateRange | null;
    transfers: PricingYearDateRange | null;
    packages: PricingYearDateRange | null;
  };
  highSeasonDates: {
    accommodation: PricingYearHighSeasonEntry[];
    courses: PricingYearHighSeasonEntry[];
    addOns: PricingYearHighSeasonEntry[];
    transfers: PricingYearHighSeasonEntry[];
    packages: PricingYearHighSeasonEntry[];
  };
}

export class DraftPricingYearExistsError extends Error {
  constructor(message: string = "A draft pricing year already exists for this division.") {
    super(message);
    this.name = "DraftPricingYearExistsError";
  }
}

const HIGH_SEASON_FALLBACK_KEYS = [
  "courses",
  "addOns",
  "transfers",
  "packages",
] as const;

export function applyHighSeasonDatesFallback(
  highSeasonDates: DraftPricingYearDetails["highSeasonDates"]
): DraftPricingYearDetails["highSeasonDates"] {
  return highSeasonDates;
}

function withHighSeasonFallback(
  payload: DraftPricingYearDetails
): DraftPricingYearDetails {
  return {
    ...payload,
    highSeasonDates: applyHighSeasonDatesFallback(payload.highSeasonDates),
  };
}

export interface DraftValidationRowIssue {
  rowIndex?: number;
  offeringId?: string | number;
  productId?: string | number;
  message?: string;
  field?: string;
}

export interface DraftValidationResult {
  canPublish?: boolean;
  summary?: { errors?: number; warnings?: number; messages?: string[] };
  rowIssues?: DraftValidationRowIssue[];
  errors?: string[];
  warnings?: string[];
}

async function handleResponse(response: Response, errorMessage: string): Promise<any> {
  if (!response.ok) {
    let detail = response.statusText || errorMessage;
    let errorMessages: string[] = [];
    let errorItems:
      | { message: string; metadata?: Record<string, any> | null }[]
      | null = null;
    
    try {
      const text = await response.text();
      if (text) {
        try {
          const json = JSON.parse(text);
          // Check if it's a structured error response with reasons/errors
          if (Array.isArray(json)) {
            const items = json
              .map((x: any) => {
                const msg = x?.message ?? x?.Message;
                if (typeof msg !== "string" || !msg.trim()) return null;
                const md = x?.metadata ?? x?.Metadata ?? null;
                return { message: msg.trim(), metadata: md && typeof md === "object" ? md : null };
              })
              .filter(Boolean) as { message: string; metadata?: Record<string, any> | null }[];

            if (items.length > 0) {
              errorItems = items;
              errorMessages = items.map((it) => {
                const centre =
                  (it.metadata as any)?.CentreName ??
                  (it.metadata as any)?.centreName ??
                  (it.metadata as any)?.centerName;
                return centre ? `${String(centre)} — ${it.message}` : it.message;
              });
            }
          } else if (json.reasons && Array.isArray(json.reasons)) {
            errorMessages = json.reasons.map((r: any) => r.message || String(r)).filter(Boolean);
          } else if (json.errors && Array.isArray(json.errors)) {
            errorMessages = json.errors.map((e: any) => e.message || String(e)).filter(Boolean);
          } else if (json.message) {
            errorMessages = [json.message];
          }
          errorMessages = Array.from(new Set(errorMessages.filter((msg: string) => msg && msg.trim())));
          if (errorMessages.length > 0) {
            detail = errorMessages.join("; ");
          } else {
            detail = text;
          }
        } catch {
          detail = text;
        }
      }
    } catch {
      // If we can't parse the response, use the default detail
    }
    
    // Check if it's a conflict (409) which might indicate a draft already exists
    if (response.status === 409) {
      throw new DraftPricingYearExistsError(detail || "A draft pricing year already exists for this division.");
    }
    
    const err = new Error(`${errorMessage} (HTTP ${response.status}) — ${detail}`.trim());
    (err as any).status = response.status;
    (err as any).errorMessages = errorMessages.length > 0 ? errorMessages : [detail];
    if (errorItems && errorItems.length > 0) {
      (err as any).errorItems = errorItems;
    }
    throw err;
  }
  
  if (response.status === 204) {
    return null;
  }
  
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    return response.json();
  }
  
  return null;
}

export async function initiateDraftPricingYear(
  divisionId: number,
  year: number,
  payload?: DraftPricingYearDetails
): Promise<void> {
  const localApi = await getLocalApi();
  const template = (apiConfig as any).pricing_years_initiate_draft as string | undefined;
  
  if (!template) {
    throw new Error("Pricing years initiate draft endpoint is not configured.");
  }
  
  const path = template.replace("{divisionId}", encodeURIComponent(String(divisionId)));
  
  // The API expects the full DraftPricingYearDetails structure
  const body = payload
    ? JSON.stringify(withHighSeasonFallback(payload))
    : JSON.stringify({
        divisionId,
        year,
        academicYearDates: {
          courses: null,
          accommodation: null,
          addOns: null,
          transfers: null,
          packages: null,
        },
        highSeasonDates: {
          accommodation: [],
          courses: [],
          addOns: [],
          transfers: [],
          packages: [],
        },
      });
  
  const response = await localApi.call(path, "POST", body);
  await handleResponse(response, "Failed to initiate draft pricing year");
}

export interface LoadDraftPricingYearParams {
  year?: number;
  schoolIds?: number[] | { id: number }[];
  productType?: string;
}

export type DraftMinimumStartDatesByProduct = {
  courses?: string;
  accommodation?: string;
  addOns?: string;
  transfers?: string;
  packages?: string;
  fees?: string;
};

const MINIMUM_START_DATE_KEYS = [
  "courses",
  "accommodation",
  "addOns",
  "transfers",
  "packages",
  "fees",
] as const;

const MINIMUM_START_DATE_API_ALIASES: Record<
  (typeof MINIMUM_START_DATE_KEYS)[number],
  string[]
> = {
  courses: [
    "minimumCourseStartDate",
    "MinimumCourseStartDate",
    "courses",
    "Courses",
    "Course",
  ],
  accommodation: [
    "minimumAccommodationStartDate",
    "MinimumAccommodationStartDate",
    "accommodation",
    "Accommodation",
  ],
  addOns: [
    "minimumAddOnStartDate",
    "MinimumAddOnStartDate",
    "addOns",
    "AddOns",
    "AddOn",
  ],
  transfers: [
    "minimumTransferStartDate",
    "MinimumTransferStartDate",
    "transfers",
    "Transfers",
    "Transfer",
  ],
  packages: [
    "minimumPackageStartDate",
    "MinimumPackageStartDate",
    "packages",
    "Packages",
    "Package",
  ],
  fees: ["minimumFeeStartDate", "MinimumFeeStartDate", "fees", "Fees", "Fee"],
};

function toMinimumStartIso(value: unknown): string | undefined {
  if (value == null) return undefined;

  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed ? trimmed.slice(0, 10) : undefined;
  }

  if (typeof value === "object") {
    const record = value as Record<string, unknown>;
    const raw =
      record.dateFrom ??
      record.DateFrom ??
      record.minimumStartDate ??
      record.MinimumStartDate ??
      record.startDate ??
      record.StartDate;

    if (typeof raw === "string") {
      const trimmed = raw.trim();
      return trimmed ? trimmed.slice(0, 10) : undefined;
    }
  }

  return undefined;
}

function mapDraftMinimumStartDates(data: unknown): DraftMinimumStartDatesByProduct {
  const src =
    data && typeof data === "object" ? (data as Record<string, unknown>) : {};

  const mapped: DraftMinimumStartDatesByProduct = {};

  for (const key of MINIMUM_START_DATE_KEYS) {
    const aliases = MINIMUM_START_DATE_API_ALIASES[key];
    for (const alias of aliases) {
      const iso = toMinimumStartIso(src[alias]);
      if (iso) {
        mapped[key] = iso;
        break;
      }
    }
  }

  return mapped;
}

export async function validateAcademicYearDatesDraft(
  divisionId: number,
  payload: DraftPricingYearDetails
): Promise<void> {
  const localApi = await getLocalApi();
  const template = (apiConfig as any)
    .pricing_years_validate_academic_year_dates_draft as string | undefined;

  if (!template) {
    throw new Error(
      "Validate academic year dates draft endpoint is not configured."
    );
  }

  const path = template.replace(
    "{divisionId}",
    encodeURIComponent(String(divisionId))
  );
  const body = JSON.stringify(withHighSeasonFallback(payload));
  const response = await localApi.call(path, "POST", body);
  await handleResponse(response, "Failed to validate academic year dates");
}

export async function getDraftMinimumStartDates(
  divisionId: number,
  year: number
): Promise<DraftMinimumStartDatesByProduct> {
  const localApi = await getLocalApi();
  const template = (apiConfig as any)
    .pricing_years_minimum_start_dates as string | undefined;

  if (!template) {
    throw new Error("Minimum start dates endpoint is not configured.");
  }

  const path = template
    .replace("{divisionId}", encodeURIComponent(String(divisionId)))
    .replace("{year}", encodeURIComponent(String(year)));

  const response = await localApi.call(path, "GET", null);

  if (response.status === 404 || response.status === 204) {
    return {};
  }

  const data = await handleResponse(
    response,
    "Failed to load draft minimum start dates"
  );

  return mapDraftMinimumStartDates(data);
}

export async function loadDraftPricingYear(
  divisionId: number,
  params?: LoadDraftPricingYearParams
): Promise<DraftPricingYearDetails | null> {
  const localApi = await getLocalApi();
  const template = (apiConfig as any).pricing_years_load_draft as string | undefined;

  if (!template) {
    return null;
  }

  const basePath = template.replace("{divisionId}", encodeURIComponent(String(divisionId)));
  const search = new URLSearchParams();
  search.set("divisionId", String(divisionId));
  if (params?.year != null) {
    search.set("year", String(params.year));
  }
  if (params?.productType != null && params.productType !== "") {
    search.set("productType", params.productType);
  }
  if (params?.schoolIds != null && params.schoolIds.length > 0) {
    const ids = params.schoolIds.map((s) => (typeof s === "object" && "id" in s ? s.id : s));
    ids.forEach((id) => search.append("schoolIds", String(id)));
  }
  const path = `${basePath}?${search.toString()}`;
  const response = await localApi.call(path, "GET", null);

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    await handleResponse(response, "Failed to load draft pricing year");
    return null;
  }

  if (response.status === 204) {
    return null;
  }

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    const data = await response.json();
    return data && typeof data === "object" ? (data as DraftPricingYearDetails) : null;
  }
  return null;
}

export async function loadDraftOfferings(
  divisionId: number,
  params: { year: number; schoolIds: { id: number }[] | number[]; productType: string }
): Promise<{ ok: boolean; data: any[]; errorMessage?: string }> {
  const localApi = await getLocalApi();
  const template = (apiConfig as any).pricing_years_load_draft as string | undefined;

  if (!template) {
    return { ok: false, data: [], errorMessage: "Load draft endpoint is not configured." };
  }

  const basePath = template.replace("{divisionId}", encodeURIComponent(String(divisionId)));
  const search = new URLSearchParams();
  search.set("divisionId", String(divisionId));
  search.set("year", String(params.year));
  if (params.productType) search.set("productType", params.productType);
  if (params.schoolIds?.length) {
    const ids = params.schoolIds.map((s) => (typeof s === "object" && "id" in s ? s.id : s));
    ids.forEach((id) => search.append("schoolIds", String(id)));
  }
  const path = `${basePath}?${search.toString()}`;
  const response = await localApi.call(path, "GET", null);

  if (!response.ok) {
    if (response.status === 404) {
      return { ok: true, data: [] };
    }
    try {
      await handleResponse(response, "Failed to load draft offerings");
    } catch (err) {
      return {
        ok: false,
        data: [],
        errorMessage: err instanceof Error ? err.message : "Failed to load draft offerings.",
      };
    }
    return { ok: false, data: [], errorMessage: "Failed to load draft offerings." };
  }

  if (response.status === 204) {
    return { ok: true, data: [] };
  }

  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    const data = await response.json();
    const list = Array.isArray(data) ? data : data?.data ?? [];
    const normalized = list.map(mapDraftApiItemToPricelistRow);
    const filtered = normalized.filter((row: any) => !hasDecommissionDateSet(row));
    return { ok: true, data: filtered };
  }
  return { ok: true, data: [] };
}

function hydrateFeeSupplementPeriodsFromApi(fee: Record<string, unknown>): unknown[] {
  const existing = fee.supplementPeriods;
  if (Array.isArray(existing) && existing.length > 0) return existing;

  const normalizedOfferingType = String(fee.feeOfferingType ?? fee.FeeOfferingType ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, "");
  const isOneOff =
    normalizedOfferingType === "oneoff" ||
    normalizedOfferingType === "oneofffee" ||
    normalizedOfferingType.includes("oneoff");
  if (isOneOff) return Array.isArray(existing) ? existing : [];

  const supplementPrices = Array.isArray(fee.supplementPrices) ? fee.supplementPrices : [];
  if (supplementPrices.length === 0) return Array.isArray(existing) ? existing : [];

  const currencyIsoCode = String(fee.currencyIsoCode ?? fee.CurrencyIsoCode ?? "").trim();
  const hasSpecificDates = !!(fee.supplementHasSpecificDates ?? fee.SupplementHasSpecificDates);
  const supplementDates = Array.isArray(fee.supplementDates) ? fee.supplementDates : [];

  const toPeriod = (
    priceEntry: Record<string, unknown>,
    startDate = "",
    endDate = ""
  ): Record<string, unknown> | null => {
    const yearVal = Number(priceEntry.year ?? priceEntry.Year);
    if (!Number.isFinite(yearVal)) return null;

    return {
      year: yearVal,
      startDate,
      endDate,
      prices: currencyIsoCode
        ? [
            {
              currencyIsoCode,
              pricePerMajorUnit: Number(
                priceEntry.pricePerMajorUnit ?? priceEntry.PricePerMajorUnit ?? 0
              ),
              pricePerMinorUnit: Number(
                priceEntry.pricePerMinorUnit ?? priceEntry.PricePerMinorUnit ?? 0
              ),
            },
          ]
        : [],
    };
  };

  if (hasSpecificDates && supplementDates.length > 0) {
    const periods: Record<string, unknown>[] = [];
    supplementDates.forEach((dateEntry: unknown, index: number) => {
      const date = (dateEntry ?? {}) as Record<string, unknown>;
      const priceEntry = (supplementPrices[index] ??
        supplementPrices[0] ??
        {}) as Record<string, unknown>;
      const period = toPeriod(
        priceEntry,
        String(date.startDate ?? date.StartDate ?? ""),
        String(date.endDate ?? date.EndDate ?? "")
      );
      if (period) periods.push(period);
    });
    return periods;
  }

  const periods: Record<string, unknown>[] = [];
  supplementPrices.forEach((priceEntry: unknown) => {
    const period = toPeriod(priceEntry as Record<string, unknown>);
    if (period) periods.push(period);
  });
  return periods;
}

function mapDraftApiItemToPricelistRow(item: any): any {
  if (!item || typeof item !== "object") return item;

  const schoolIds = Array.isArray(item.schoolIds) ? item.schoolIds : [];
  const product = item.product;
  const fee = item.fee;

  if (product && typeof product === "object") {
    const typeName = product.productId?.type?.name ?? product.productId?.type ?? "Accommodation";
    const offeringType = typeName === "Package" ? "package" : "standard";
    const dateStrategies = Array.isArray(product.dateStrategies) ? product.dateStrategies : [];
    const dateStrategyAssignments = mapDateStrategyAssignments(dateStrategies);

    const rawPeriods = Array.isArray(product.pricePeriods) ? product.pricePeriods : [];
    const pricePeriods = rawPeriods.map((pp: any) => {
      const bands = Array.isArray(pp?.priceBands) ? pp.priceBands : [];
      const hasFlatCurrencyFields = bands.some(
        (b: any) =>
          b &&
          typeof b === "object" &&
          ("currencyIsoCode" in b || "pricePerMajorUnit" in b || "pricePerMinorUnit" in b) &&
          !Array.isArray((b as any).prices),
      );

      const priceBands = bands.length
        ? hasFlatCurrencyFields
          ? (() => {
              const byRange = new Map<
                string,
                {
                  minUnits: number;
                  maxUnits: number;
                  prices: { currencyIsoCode: string; pricePerMajorUnit: number; pricePerMinorUnit: number }[];
                }
              >();

              bands.forEach((b: any) => {
                const minUnits = Number(b?.minUnits ?? 1);
                const maxUnits = Number(b?.maxUnits ?? 2147483647);
                const key = `${minUnits}|${maxUnits}`;
                const entry =
                  byRange.get(key) ??
                  {
                    minUnits,
                    maxUnits,
                    prices: [],
                  };

                const currencyIsoCode = String(b?.currencyIsoCode ?? "").trim();
                if (currencyIsoCode) {
                  entry.prices.push({
                    currencyIsoCode,
                    pricePerMajorUnit: Number(b?.pricePerMajorUnit ?? 0),
                    pricePerMinorUnit: Number(b?.pricePerMinorUnit ?? 0),
                  });
                }

                byRange.set(key, entry);
              });

              return [...byRange.values()];
            })()
          : bands
        : [
            {
              minUnits: 1,
              maxUnits: 2147483647,
              prices: [] as {
                currencyIsoCode: string;
                pricePerMajorUnit: number;
                pricePerMinorUnit: number;
              }[],
            },
          ];
      return {
        startDate: pp?.startDate,
        endDate: pp?.endDate,
        priceBandStrategyId: pp?.priceBandStrategyId ?? 1,
        priceBands,
      };
    });

    const row: any = {
      type: typeName,
      offeringType,
      productId: product.productId,
      name: product.name,
      productIdText: product.productIdText,
      schoolIds,
      pricePeriods,
      dateStrategyAssignments,
      isUpdated: !!product.isUpdated,
      isNew: !!product.isNew,
      isDeleted: !!product.isDeleted,
      hasPendingChanges: !!product.hasPendingChanges,
      decommissionDate: product.decommissionDate ?? null,
      schoolStatuses: product.schoolStatuses ?? [],
    };
    if (offeringType === "package") {
      row.packageOfferingBreakdowns = mapPackageOfferingBreakdowns(product);
    }
    return row;
  }

  if (fee && typeof fee === "object") {
    const existingStatuses = Array.isArray(fee.schoolStatuses)
      ? fee.schoolStatuses
      : [];
      
    const schoolStatuses =
      existingStatuses.length > 0
        ? existingStatuses
        : schoolIds
            .map((s: any) => {
              const id =
                typeof s === "object" && s != null && "id" in s
                  ? Number(s.id)
                  : Number(s);
              if (!Number.isFinite(id)) return null;
              return {
                schoolId: id,
                status: "Active" as const,
                isDeleted: false,
              };
            })
            .filter(Boolean) as {
            schoolId: number;
            status: "Active";
            isDeleted: boolean;
          }[];

    const rawFeeId = fee.feeId;
    const feeIdNormalized =
      rawFeeId != null &&
      typeof rawFeeId === "object" &&
      rawFeeId !== null &&
      "id" in rawFeeId
        ? rawFeeId
        : {
            id:
              typeof rawFeeId === "number" || typeof rawFeeId === "string"
                ? Number(rawFeeId)
                : Number((rawFeeId as { id?: number })?.id),
          };

    const feeUnitTypeId = resolveFeeUnitTypeId(fee as Record<string, unknown>);

    const productIdText =
      fee.productIdText ||
      (fee.productId?.id != null ? `Fee-${fee.productId.id}` : "");

    const oneOffFeePrices = (fee.oneOffFeePrices ?? []).filter(
      (x: unknown) => x != null,
    );
    const supplementPeriods = hydrateFeeSupplementPeriodsFromApi(
      fee as Record<string, unknown>
    );

    return {
      ...fee,
      schoolIds,
      schoolStatuses,
      feeId: feeIdNormalized,
      ...(feeUnitTypeId != null ? { feeUnitTypeId } : {}),
      ...(productIdText ? { productIdText } : {}),
      oneOffFeePrices,
      supplementPeriods,
      type: "Fee",
      offeringType: "fee",
    };
  }

  return { ...item, schoolIds };
}

export async function saveDraftPricingYear(
  divisionId: number,
  payload: DraftPricingYearDetails
): Promise<void> {
  const localApi = await getLocalApi();
  const template = (apiConfig as any).pricing_years_initiate_draft as string | undefined;
  
  if (!template) {
    throw new Error("Pricing years initiate draft endpoint is not configured.");
  }
  
  const path = template.replace("{divisionId}", encodeURIComponent(String(divisionId)));
  const body = JSON.stringify(withHighSeasonFallback(payload));

  const response = await localApi.call(path, "POST", body);
  await handleResponse(response, "Failed to save draft pricing year");
}

export async function updateDraftPricingYear(
  divisionId: number,
  payload: DraftPricingYearDetails
): Promise<void> {
  const localApi = await getLocalApi();
  const template = (apiConfig as any).pricing_years_update_draft as string | undefined;

  if (!template) {
    throw new Error("Pricing years update draft endpoint is not configured.");
  }

  const path = template.replace("{divisionId}", encodeURIComponent(String(divisionId)));
  const body = JSON.stringify(withHighSeasonFallback(payload));
  const response = await localApi.call(path, "POST", body);
  await handleResponse(response, "Failed to update draft pricing year");
}

export async function saveDraftPricelistContent(
  divisionId: number,
  year: number,
  pricelist: any[],
  productType: string
): Promise<void> {
  const localApi = await getLocalApi();
  const template = (apiConfig as any).pricing_years_save_draft as string | undefined;

  if (!template) {
    throw new Error("Pricing years save draft endpoint is not configured.");
  }

  const toDraftSaveItems = (rows: any[]): any[] => {
    if (!Array.isArray(rows)) return [];

    const toSchoolIds = (src: any): { id: number }[] => {
      const raw = Array.isArray(src?.schoolIds) ? src.schoolIds : [];
      return raw
        .map((s: any) => {
          const v = typeof s === "object" && s != null ? (s.id ?? s.Id ?? s.schoolId ?? s.SchoolId) : s;
          const n = Number(v);
          return Number.isFinite(n) ? { id: n } : null;
        })
        .filter(Boolean) as { id: number }[];
    };

    const getFirstCurrencyIso = (src: any): string => {
      const isoFromRoot = src?.currencyIsoCode ?? src?.CurrencyIsoCode;
      if (typeof isoFromRoot === "string" && isoFromRoot.trim()) return isoFromRoot.trim();

      const periods = Array.isArray(src?.supplementPeriods) ? src.supplementPeriods : [];
      for (const p of periods) {
        const prices = Array.isArray(p?.prices) ? p.prices : [];
        const first = prices.find((x: any) => typeof x?.currencyIsoCode === "string" && x.currencyIsoCode.trim());
        if (first) return String(first.currencyIsoCode).trim();
      }

      const oneOff = Array.isArray(src?.oneOffFeePrices) ? src.oneOffFeePrices : [];
      const firstOneOff = oneOff.find((x: any) => typeof x?.currencyIsoCode === "string" && x.currencyIsoCode.trim());
      if (firstOneOff) return String(firstOneOff.currencyIsoCode).trim();

      return "";
    };

    const flattenPriceBands = (pp: any): any[] => {
      const bands = Array.isArray(pp?.priceBands ?? pp?.PriceBands)
        ? (pp.priceBands ?? pp.PriceBands)
        : [];

      const flat: any[] = [];
      for (const b of bands) {
        const minUnits = Number(b?.minUnits ?? b?.MinUnits ?? 0);
        const maxUnits = Number(b?.maxUnits ?? b?.MaxUnits ?? 0);
        const prices = Array.isArray(b?.prices) ? b.prices : null;

        if (prices && prices.length) {
          for (const p of prices) {
            flat.push({
              minUnits,
              maxUnits,
              currencyIsoCode: p?.currencyIsoCode ?? p?.CurrencyIsoCode ?? "",
              pricePerMajorUnit: Number(p?.pricePerMajorUnit ?? p?.PricePerMajorUnit ?? 0),
              pricePerMinorUnit: Number(p?.pricePerMinorUnit ?? p?.PricePerMinorUnit ?? 0),
            });
          }
        } else {
          flat.push({
            minUnits,
            maxUnits,
            currencyIsoCode: b?.currencyIsoCode ?? b?.CurrencyIsoCode ?? "",
            pricePerMajorUnit: Number(b?.pricePerMajorUnit ?? b?.PricePerMajorUnit ?? 0),
            pricePerMinorUnit: Number(b?.pricePerMinorUnit ?? b?.PricePerMinorUnit ?? 0),
          });
        }
      }
      return flat;
    };

    const mapDateStrategies = (src: any): any[] => {
      const list = Array.isArray(src?.dateStrategies)
        ? src.dateStrategies
        : Array.isArray(src?.dateStrategyAssignments)
          ? src.dateStrategyAssignments
          : [];

      const mapSpecificStartDates = (raw: any): any[] => {
        const arr = Array.isArray(raw) ? raw : [];
        return arr
          .map((x: any) => {
            if (x == null) return null;
            if (typeof x === "string" || x instanceof Date) {
              return { date: x as any, isNew: false, isDeleted: false };
            }
            if (typeof x === "object") {
              return {
                date: (x as any).date ?? (x as any).Date ?? x,
                isNew: !!((x as any).isNew ?? (x as any).IsNew),
                isDeleted: !!((x as any).isDeleted ?? (x as any).IsDeleted),
              };
            }
            return null;
          })
          .filter(Boolean);
      };

      const mapFixedDurationDates = (raw: any): any[] => {
        const arr = Array.isArray(raw) ? raw : [];
        return arr
          .map((x: any) => {
            if (!x || typeof x !== "object") return null;
            return {
              startDate: (x as any).startDate ?? (x as any).StartDate ?? null,
              endDate: (x as any).endDate ?? (x as any).EndDate ?? null,
              isNew: !!((x as any).isNew ?? (x as any).IsNew),
              isDeleted: !!((x as any).isDeleted ?? (x as any).IsDeleted),
            };
          })
          .filter(Boolean);
      };

      return list.map((ds: any) => ({
        id: ds?.id,
        strategyTypeId: ds?.strategyTypeId,
        fromDate: ds?.fromDate,
        toDate: ds?.toDate ?? null,
        lastStartDate: ds?.lastStartDate ?? null,
        recurranceStartDays: normalizeRecurranceStartDays(
          ds?.recurranceStartDays ?? ds?.RecurranceStartDays
        ),
        specificStartDates: mapSpecificStartDates(ds?.specificStartDates),
        fixedDurationDates: mapFixedDurationDates(ds?.fixedDurationDates),
        originalFromDate: ds?.originalFromDate ?? ds?.fromDate,
        originalToDate: ds?.originalToDate ?? ds?.toDate ?? null,
        isArchived: !!ds?.isArchived,
      }));
    };

    const mapApplicableProducts = (raw: any): any[] => {
      const arr = Array.isArray(raw) ? raw : [];
      return arr.map((ap: any) => ({
        productTypeName: ap?.productTypeName ?? ap?.productType ?? ap?.productTypeId ?? null,
        selectionMode: ap?.selectionMode ?? null,
        productIds: Array.isArray(ap?.productIds) ? ap.productIds : [],
        accommodationTypeIds: Array.isArray(ap?.accommodationTypeIds) ? ap.accommodationTypeIds : [],
        addOnTypeIds: Array.isArray(ap?.addOnTypeIds) ? ap.addOnTypeIds : [],
        minDuration: ap?.minDuration ?? null,
        maxDuration: ap?.maxDuration ?? null,
      }));
    };

    const mapFeeBlock = (feeRow: any): any => {
      const feeIdValue =
        typeof feeRow?.feeId === "object" && feeRow?.feeId != null
          ? Number(feeRow.feeId.id)
          : Number(feeRow?.feeId);

      const currencyIsoCode = getFirstCurrencyIso(feeRow);
      const rawOfferingType = feeRow?.feeOfferingType ?? feeRow?.FeeOfferingType ?? "";
      const normalizedOfferingType = String(rawOfferingType).trim().toLowerCase().replace(/[\s_-]+/g, "");
      const isOneOff =
        normalizedOfferingType === "oneoff" ||
        normalizedOfferingType === "oneofffee" ||
        normalizedOfferingType.includes("oneoff");

      const hasSpecificDates = !isOneOff && !!(feeRow?.supplementHasSpecificDates ?? feeRow?.SupplementHasSpecificDates);
      const rawPeriods = Array.isArray(feeRow?.supplementPeriods) ? feeRow.supplementPeriods : [];

      const supplementDates = hasSpecificDates
        ? rawPeriods.map((p: any) => ({
            startDate: p?.startDate ?? null,
            endDate: p?.endDate ?? null,
          }))
        : [];

      const supplementPrices = rawPeriods
        .map((p: any) => {
          const yearVal = Number(p?.year);
          if (!Number.isFinite(yearVal)) return null;
          const prices = Array.isArray(p?.prices) ? p.prices : [];
          const pick =
            prices.find((x: any) => String(x?.currencyIsoCode ?? "").trim() === currencyIsoCode) ??
            prices[0];
          return {
            year: yearVal,
            pricePerMajorUnit: Number(pick?.pricePerMajorUnit ?? 0),
            pricePerMinorUnit: Number(pick?.pricePerMinorUnit ?? 0),
          };
        })
        .filter(Boolean);

      const oneOffFeePrices = (Array.isArray(feeRow?.oneOffFeePrices) ? feeRow.oneOffFeePrices : [])
        .map((x: any) => {
          const yearVal = Number(x?.year);
          if (!Number.isFinite(yearVal)) return null;
          const matchesCurrency =
            currencyIsoCode && String(x?.currencyIsoCode ?? "").trim() === currencyIsoCode;
          const amountVal = Number(x?.amount ?? 0);
          return matchesCurrency || !x?.currencyIsoCode
            ? { year: yearVal, amount: amountVal, currencyIsoCode }
            : null;
        })
        .filter(Boolean);

      return {
        isUpdated: !!feeRow?.isUpdated && !feeRow?.isNew,
        isNew: !!feeRow?.isNew,
        isDeleted: !!feeRow?.isDeleted,
        hasPendingChanges: !!feeRow?.hasPendingChanges || !!feeRow?.isUpdated || !!feeRow?.isNew,
        toValidate: feeRow?.toValidate ?? true,
        colorHEX: feeRow?.colorHEX ?? null,

        productId: feeRow?.productId ?? null,
        name: feeRow?.name ?? "",
        feeId: Number.isFinite(feeIdValue) ? feeIdValue : 0,
        feeOfferingType: rawOfferingType,
        currencyIsoCode,

        activeFromPricingYear: feeRow?.activeFromPricingYear ?? null,
        activeToPricingYear: feeRow?.activeToPricingYear ?? null,
        minimumAge: feeRow?.minimumAge ?? null,
        maximumAge: feeRow?.maximumAge ?? null,

        supplementHasSpecificDates: hasSpecificDates,
        supplementDates,

        supplementMaximumUnits: isOneOff
          ? null
          : (feeRow?.supplementMaximumUnits ?? {
              hasMaximum: false,
              majorUnits: 0,
              minorUnits: 0,
            }),
        supplementPrices: isOneOff ? [] : supplementPrices,

        oneOffFeePrices,
        applicableProducts: mapApplicableProducts(feeRow?.applicableProducts),
      };
    };

    return rows.map((row: any) => {
      const schoolIds = toSchoolIds(row);
      const isFee = row?.type === "Fee" || row?.offeringType === "fee" || row?.feeId != null;

      if (isFee) {
        return { schoolIds, fee: mapFeeBlock(row) };
      }

      const pricePeriods = Array.isArray(row?.pricePeriods) ? row.pricePeriods : [];
      const mappedPricePeriods = pricePeriods.map((pp: any) => ({
        startDate: pp?.startDate ?? pp?.StartDate ?? null,
        endDate: pp?.endDate ?? pp?.EndDate ?? null,
        priceBandStrategyId: Number(pp?.priceBandStrategyId ?? pp?.PriceBandStrategyId ?? 0),
        priceBands: flattenPriceBands(pp),
      }));

      return {
        schoolIds,
        product: {
          isUpdated: !!row?.isUpdated && !row?.isNew,
          isNew: !!row?.isNew,
          isDeleted: !!row?.isDeleted,
          hasPendingChanges: !!row?.hasPendingChanges || !!row?.isUpdated || !!row?.isNew,
          toValidate: row?.toValidate ?? true,
          colorHEX: row?.colorHEX ?? null,
          productId: row?.productId,
          name: row?.name,
          pricePeriods: mappedPricePeriods,
          dateStrategies: mapDateStrategies(row),
          decommissionDate: row?.decommissionDate ?? null,
          productIdText: row?.productIdText,
        },
      };
    });
  };

  const search = new URLSearchParams();
  search.set("year", String(year));
  if (productType != null && productType !== "") {
    search.set("productType", productType);
  }
  const path =
    template.replace("{divisionId}", encodeURIComponent(String(divisionId))) +
    "?" + search.toString();
  const body = JSON.stringify(toDraftSaveItems(Array.isArray(pricelist) ? pricelist : []));
  const response = await localApi.call(path, "POST", body);
  await handleResponse(response, "Failed to save draft pricelist content");
}

/**
 * Validates draft pricing year for a specific product type.
 * @param divisionId - The division ID
 * @param year - The pricing year
 * @param productType - The product type to validate
 * @param pricelistData - Array of pricelist items to validate
 * @returns The validation response (array of pricelist items with validation results)
 */
export async function validateDraftPricingYear(
  divisionId: number,
  year: number,
  productType: string,
  pricelistData: any[]
): Promise<any[]> {
  const localApi = await getLocalApi();
  const template = (apiConfig as any).pricing_years_validate_draft as string | undefined;
  
  if (!template) {
    throw new Error("Pricing years validate draft endpoint is not configured.");
  }
  
  const path = template
    .replace("{divisionId}", encodeURIComponent(String(divisionId)))
    + `?year=${encodeURIComponent(String(year))}&productType=${encodeURIComponent(productType)}`;
  
  const body = JSON.stringify(pricelistData);
  const response = await localApi.call(path, "POST", body);
  const data = await handleResponse(response, "Failed to validate draft pricing year");
  return Array.isArray(data) ? data : [];
}

/**
 * Fully validates draft pricing year (all product types).
 * @param divisionId - The division ID
 * @param year - The pricing year
 * @returns The validation response
 */
export async function fullyValidateDraftPricingYear(
  divisionId: number,
  year: number
): Promise<DraftValidationResult> {
  const localApi = await getLocalApi();
  const template = (apiConfig as any).pricing_years_fully_validate_draft as string | undefined;
  
  if (!template) {
    throw new Error("Pricing years fully validate draft endpoint is not configured.");
  }
  
  const path = template
    .replace("{divisionId}", encodeURIComponent(String(divisionId)))
    + `?year=${encodeURIComponent(String(year))}`;
  
  const response = await localApi.call(path, "POST", null);
  const data = await handleResponse(response, "Failed to fully validate draft pricing year");

  // API returns 204
  if (data == null) {
    return {
      canPublish: true,
      summary: { errors: 0, warnings: 0, messages: ["Validation passed."] },
      errors: [],
      warnings: [],
      rowIssues: [],
    };
  }

  return data as DraftValidationResult;
}

/**
 * Publishes draft pricing year for the specified division.
 * @param divisionId - The division ID
 * @param year - The pricing year to publish
 * @returns The published pricelist data
 */
export async function publishDraftPricingYear(
  divisionId: number,
  year: number
): Promise<any[]> {
  const localApi = await getLocalApi();
  const template = (apiConfig as any).pricing_years_publish_draft as string | undefined;
  
  if (!template) {
    throw new Error("Pricing years publish draft endpoint is not configured.");
  }
  
  const path = template
    .replace("{divisionId}", encodeURIComponent(String(divisionId)))
    + `?year=${encodeURIComponent(String(year))}`;
  
  const response = await localApi.call(path, "PUT", null);
  const data = await handleResponse(response, "Failed to publish draft pricing year");
  return Array.isArray(data) ? data : [];
}